import { recoveryEnabled } from "@/api/recovery"
import { RecoveryReview } from "@/components/RecoveryReview"
import { RecoveryContactPrompt } from "@/components/RecoveryContactPrompt"
import React, { useMemo, useState } from "react"
import {
    GrantApplication,
    createApplication,
    useFetchApplications,
    useArchiveApplications,
    useUnarchiveApplications,
    useApplicationSettings,
} from "@/api/applicationQueries"
import { columns } from "@/components/DataTable/Column"
import { DataTable } from "@/components/DataTable/DataTable"
import { BulkDeclineModal } from "@/components/BulkDeclineModal/BulkDeclineModal"
import { ApplicationSettingsButton } from "@/components/ApplicationDeadlineSettings/ApplicationSettingsButton"

import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

import {
    SortingState,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { useGetUserById } from "@/api/userQueries"
import { TransferRequestAlert } from "@/components/TransferRequestAlert"
import { Loader, Info, TriangleAlert } from "lucide-react"
import { APP_ROUTES } from "@/common/urls"
import { cn } from "@/lib/utils"
import { toast } from "react-hot-toast"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import {
    CategorySelect,
    CountrySelect,
    AgeGroupSelect,
    VoteRatingSelect,
} from "@/components/Filters"
import TablePageNavigationButtons from "@/components/DataTable/TablePaginationNavigationButtons"

const columnRoleOrder: Record<string, string[]> = {
    admin: [
        "dateSubmitted",
        "category",
        "orgName",
        "reviewStatus",
        "votingStatus",
        "charityNumber",
        "requestedAmount",
        "grantAmount",
        "country",
        "territory",
        "ageGroup",
        "veteransCheck",
        "sector",
        "subsector",
        "dateDecide",
        "applicantNotified",
        "votingDeadline",
    ],
    trustee: [
        "category",
        "orgName",
        "requestedAmount",
        "votingStatus",
        "country",
        "territory",
        "ageGroup",
        "veteransCheck",
        "sector",
        "subsector",
        "dateSubmitted",
        "dateDecide",
        "votingDeadline",
    ],
    applicant: [
        "status",
        "charityNumber",
        "orgName",
        "requestedAmount",
        "dateCreated",
        "dateSubmitted",
        "territory",
        "ageGroup",
        "veteransCheck",
        "sector",
        "subsector",
    ],
}

type Filters = {
    category?: string
    reviewStatus?: string
    country?: string
    ageGroup?: string
    showArchived?: boolean
    voteRating?: string
}

export default function AppDashboard() {
    const navFunc = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams()
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [creatingApplication, setCreatingApplication] = React.useState(false)
    const [selectedApplications, setSelectedApplications] = useState<string[]>(
        []
    )
    const [isBulkDeclineModalOpen, setIsBulkDeclineModalOpen] = useState(false)

    const { data: settings } = useApplicationSettings()

    const reviewStatus = searchParams.get("reviewStatus") || undefined
    const country = searchParams.get("country") || undefined
    const category = searchParams.get("category") || undefined
    const ageGroup = searchParams.get("ageGroup") || undefined
    const showArchived = searchParams.get("showArchived") === "true"
    const voteRating = searchParams.get("voteRating") || undefined

    const advancedFilters: Filters = {
        category,
        country,
        ageGroup,
        reviewStatus,
        showArchived,
        voteRating,
    }

    const userDetails = useGetUserById(navFunc)
    const applications = useFetchApplications({
        userRole: userDetails.data?.role || "",
        userId: userDetails.data?.userId || "",
        filters: advancedFilters,
    })

    const { mutate: archiveApplications } = useArchiveApplications()
    const { mutate: unarchiveApplications } = useUnarchiveApplications()

    // Move hasDeclinedApplications here with other hooks
    const hasDeclinedApplications = useMemo(() => {
        if (!applications.data || selectedApplications.length === 0)
            return false
        return applications.data.some(
            (app) =>
                selectedApplications.includes(app.applicationId || "") &&
                app.reviewStatus === "declined"
        )
    }, [applications.data, selectedApplications])

    // Make getDeclinableApplicationIds a memoized value
    const declinableApplicationIds = useMemo(() => {
        if (!applications.data) return []
        return selectedApplications.filter((id) => {
            const app = applications.data.find((a) => a.applicationId === id)
            return app && app.reviewStatus !== "declined"
        })
    }, [applications.data, selectedApplications])

    // Add this new memoized value to get declined application names
    const declinedApplicationNames = useMemo(() => {
        if (!applications.data || selectedApplications.length === 0) return []
        return applications.data
            .filter(
                (app) =>
                    selectedApplications.includes(app.applicationId || "") &&
                    app.reviewStatus === "declined"
            )
            .map((app) => app.orgName || "Unnamed Organization")
    }, [applications.data, selectedApplications])

    // useMemo to Filter columns based on user role
    const filteredColumns = useMemo(() => {
        const role = userDetails.data?.role
        const filtered = columns.filter(
            (column) =>
                role && (!column.userRoles || column.userRoles.includes(role))
        )

        const order = columnRoleOrder[role || "applicant"] || []
        return filtered.sort(
            (a, b) =>
                order.indexOf(a.accessorKey as string) -
                order.indexOf(b.accessorKey as string)
        )
    }, [userDetails.data?.role])

    // Create the table instance
    const table = useReactTable<GrantApplication>({
        data: applications.data ?? [],
        columns: filteredColumns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
        },
        initialState: {
            pagination: {
                pageSize: 30,
            },
            columnVisibility: {
                applicationId: false,
            },
        },
    })

    if (applications.isError) {
        return <div>Error fetching applications</div>
    }

    if (applications.isFetching || !applications.data) {
        return (
            <div className="h-[60dvh] sm:py-[30dvh] flex justify-center items-center gap-4">
                <h1 className="text-3xl font-bold text-app-defaultLight">
                    Loading Data
                </h1>
                <Loader
                    size={32}
                    className="text-app-defaultLight animate-spin"
                />
            </div>
        )
    }

    // Function to update the URL search parameters for advanced filters
    function setFilterParams(key: string, value: string) {
        const newSearchParams = new URLSearchParams(searchParams)

        if (value === "all") {
            newSearchParams.delete(key)
        } else {
            newSearchParams.set(key, value)
        }

        // When changing review status, reset showArchived
        if (key === "reviewStatus") {
            newSearchParams.set("showArchived", "false")
        }
        // When toggling archive view, clear review status
        if (key === "showArchived") {
            if (value === "true") {
                newSearchParams.delete("reviewStatus")
            }
        }

        // Clear any selected rows when changing filters
        setSelectedApplications([])

        setSearchParams(newSearchParams)
    }

    async function createNewApplication() {
        if (!userDetails.data) return

        setCreatingApplication(false)

        try {
            // Create a new application in Firebase
            setCreatingApplication(true)
            const applicationId = await createApplication(userDetails.data)
            navFunc(APP_ROUTES.ROOT.APPLICATION.replace(":id", applicationId))
        } catch (error) {
            if (
                error instanceof Error &&
                error.message.includes(
                    "Applications are currently not being accepted."
                )
            ) {
                toast.error("Applications are currently not being accepted.")
            } else {
                console.error("Error creating new application:", error)
            }
        } finally {
            setCreatingApplication(false)
        }
    }

    const handleArchiveSelected = () => {
        if (selectedApplications.length > 0) {
            archiveApplications(selectedApplications)
            setSelectedApplications([])
        }
    }

    const handleUnarchiveSelected = () => {
        if (selectedApplications.length > 0) {
            unarchiveApplications(selectedApplications)
            setSelectedApplications([])
        }
    }

    const handleSelectionChange = (selectedRows: string[]) => {
        setSelectedApplications(selectedRows)
    }

    const handleBulkDeclineSuccess = () => {
        setSelectedApplications([])
    }

    const titleText = (() => {
        switch (userDetails.data?.role) {
            case "admin":
                return "Manage Applications"
            case "trustee":
                return "Application Voting"
            case "applicant":
                return "My Applications"
            default:
                return "Applications"
        }
    })()

    return (
        <>
            {!!settings?.deadline && (
                <span className="text-sm py-2 px-12 w-full flex items-center justify-center bg-yellow-500/10 text-orange-500">
                    <TriangleAlert className="inline-block mr-2 text-orange-500 flex-shrink-0" />
                    New submissions are going to be accepted until{" "}
                    {settings?.deadline?.toDate().toLocaleTimeString()} on{" "}
                    {settings?.deadline?.toDate().toLocaleDateString()}
                </span>
            )}
            {settings?.isAcceptingApplications === false && (
                <span className="text-sm py-2 px-12 w-full flex items-center justify-center bg-red-500/10 text-red-500">
                    <TriangleAlert className="inline-block mr-2 text-red-500 flex-shrink-0" />
                    New submissions are no longer being accepted at this time
                </span>
            )}

            <div className="flex flex-col gap-4 pb-20 pt-8">
                <div className="container border-b border-input py-4 flex justify-between items-center">
                    <div className="flex items-center gap-2 justify-between w-full">
                        <h1 className="text-2xl font-semibold">{titleText}</h1>
                        {userDetails.data?.role === "admin" && (
                            <ApplicationSettingsButton />
                        )}
                    </div>
                    <div className="flex gap-2">
                        {userDetails.data?.role === "applicant" && (
                            <Button onClick={createNewApplication}>
                                {creatingApplication
                                    ? "Creating..."
                                    : "Create Application"}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="flex container flex-col gap-10 px-6">
                    {recoveryEnabled &&
                        userDetails.data?.role === "applicant" && (
                            <RecoveryContactPrompt />
                        )}
                    {userDetails.data?.role === "admin" && (
                        <>
                            <TransferRequestAlert />
                            <RecoveryReview />
                            <section className="container bg-white/50 backdrop-blur-lg sm:sticky z-10 sm:top-20 flex flex-col gap-4 bg-white rounded-xl shadow-lg px-6 py-4 border border-input">
                                <div className="flex justify-between items-center">
                                    <div className="flex flex-wrap gap-2">
                                        <Select
                                            defaultValue={
                                                searchParams.get(
                                                    "reviewStatus"
                                                ) ?? "all"
                                            }
                                            onValueChange={(value) => {
                                                if (value === "archived") {
                                                    setFilterParams(
                                                        "showArchived",
                                                        "true"
                                                    )
                                                } else {
                                                    setFilterParams(
                                                        "reviewStatus",
                                                        value
                                                    )
                                                }
                                            }}
                                            value={
                                                showArchived
                                                    ? "archived"
                                                    : reviewStatus
                                            }
                                        >
                                            <SelectTrigger className="flex gap-2 sm:hidden rounded-full bg-app-defaultLight text-white">
                                                <span className="font-semibold">
                                                    Status:&nbsp;
                                                </span>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">
                                                    All
                                                </SelectItem>
                                                <SelectItem value="new">
                                                    New
                                                </SelectItem>
                                                <SelectItem value="review">
                                                    Review
                                                </SelectItem>
                                                <SelectItem value="accepted">
                                                    Approved
                                                </SelectItem>
                                                <SelectItem value="declined">
                                                    Declined
                                                </SelectItem>
                                                {userDetails.data?.role ===
                                                    "admin" && (
                                                    <SelectItem value="archived">
                                                        Archived
                                                    </SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <div className="hidden sm:flex flex-row items-center overflow-clip rounded-l-full rounded-r-full">
                                            <Button
                                                className={cn(
                                                    "rounded-none",
                                                    !reviewStatus &&
                                                        !showArchived &&
                                                        "bg-primary/90"
                                                )}
                                                onClick={() =>
                                                    setFilterParams(
                                                        "reviewStatus",
                                                        "all"
                                                    )
                                                }
                                            >
                                                All
                                            </Button>
                                            <Button
                                                className={cn(
                                                    "rounded-none",
                                                    reviewStatus === "new" &&
                                                        !showArchived &&
                                                        "bg-primary/90"
                                                )}
                                                onClick={() =>
                                                    setFilterParams(
                                                        "reviewStatus",
                                                        "new"
                                                    )
                                                }
                                            >
                                                New
                                            </Button>
                                            <Button
                                                className={cn(
                                                    "rounded-none",
                                                    reviewStatus === "review" &&
                                                        !showArchived &&
                                                        "bg-primary/90"
                                                )}
                                                onClick={() =>
                                                    setFilterParams(
                                                        "reviewStatus",
                                                        "review"
                                                    )
                                                }
                                            >
                                                Review
                                            </Button>
                                            <Button
                                                className={cn(
                                                    "rounded-none",
                                                    reviewStatus ===
                                                        "accepted" &&
                                                        !showArchived &&
                                                        "bg-primary/90"
                                                )}
                                                onClick={() =>
                                                    setFilterParams(
                                                        "reviewStatus",
                                                        "accepted"
                                                    )
                                                }
                                            >
                                                Approved
                                            </Button>
                                            <Button
                                                className={cn(
                                                    "rounded-none",
                                                    reviewStatus ===
                                                        "declined" &&
                                                        !showArchived &&
                                                        "bg-primary/90"
                                                )}
                                                onClick={() =>
                                                    setFilterParams(
                                                        "reviewStatus",
                                                        "declined"
                                                    )
                                                }
                                            >
                                                Declined
                                            </Button>
                                            <Button
                                                className={cn(
                                                    "rounded-none",
                                                    showArchived &&
                                                        "bg-primary/90",
                                                    userDetails.data?.role !==
                                                        "admin" && "hidden"
                                                )}
                                                onClick={() =>
                                                    setFilterParams(
                                                        "showArchived",
                                                        showArchived
                                                            ? "false"
                                                            : "true"
                                                    )
                                                }
                                            >
                                                Archived
                                            </Button>
                                        </div>
                                    </div>
                                    <TablePageNavigationButtons table={table} />
                                </div>
                                <div className="flex flex-wrap justify-between items-center gap-4">
                                    <div
                                        className={cn(
                                            "flex flex-wrap justify-start items-center gap-4"
                                        )}
                                    >
                                        <CategorySelect
                                            value={category || "all"}
                                            onValueChange={(value) =>
                                                setFilterParams(
                                                    "category",
                                                    value
                                                )
                                            }
                                        />
                                        <CountrySelect
                                            value={country || "all"}
                                            onValueChange={(value) =>
                                                setFilterParams(
                                                    "country",
                                                    value
                                                )
                                            }
                                        />
                                        <AgeGroupSelect
                                            value={ageGroup || "all"}
                                            onValueChange={(value) =>
                                                setFilterParams(
                                                    "ageGroup",
                                                    value
                                                )
                                            }
                                        />
                                        <VoteRatingSelect
                                            value={voteRating || "all"}
                                            onValueChange={(value) =>
                                                setFilterParams(
                                                    "voteRating",
                                                    value
                                                )
                                            }
                                        />
                                    </div>

                                    <div>
                                        <div className="flex flex-wrap justify-start items-center gap-4">
                                            {userDetails.data?.role ===
                                                "admin" &&
                                                selectedApplications.length >
                                                    0 && (
                                                    <>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={
                                                                showArchived
                                                                    ? handleUnarchiveSelected
                                                                    : handleArchiveSelected
                                                            }
                                                        >
                                                            {showArchived
                                                                ? "Unarchive"
                                                                : "Archive"}{" "}
                                                            Selected (
                                                            {
                                                                selectedApplications.length
                                                            }
                                                            )
                                                        </Button>
                                                        {!showArchived && (
                                                            <>
                                                                {hasDeclinedApplications ? (
                                                                    <TooltipProvider>
                                                                        <Tooltip>
                                                                            <TooltipTrigger
                                                                                asChild
                                                                            >
                                                                                <span className="inline-flex">
                                                                                    <Button
                                                                                        variant="decline"
                                                                                        onClick={() =>
                                                                                            setIsBulkDeclineModalOpen(
                                                                                                true
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            hasDeclinedApplications
                                                                                        }
                                                                                    >
                                                                                        Decline
                                                                                        Selected
                                                                                    </Button>
                                                                                </span>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent
                                                                                side="bottom"
                                                                                align="center"
                                                                            >
                                                                                Cannot
                                                                                decline
                                                                                applications
                                                                                that
                                                                                are
                                                                                already
                                                                                declined:{" "}
                                                                                <br />{" "}
                                                                                {declinedApplicationNames
                                                                                    .slice(
                                                                                        0,
                                                                                        3
                                                                                    )
                                                                                    .join(
                                                                                        ", "
                                                                                    )}{" "}
                                                                                {declinedApplicationNames.length >
                                                                                    3 &&
                                                                                    `and ${declinedApplicationNames.length - 3} more`}
                                                                            </TooltipContent>
                                                                        </Tooltip>
                                                                    </TooltipProvider>
                                                                ) : (
                                                                    <Button
                                                                        variant="decline"
                                                                        onClick={() =>
                                                                            setIsBulkDeclineModalOpen(
                                                                                true
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            hasDeclinedApplications
                                                                        }
                                                                    >
                                                                        Decline
                                                                        Selected
                                                                        (
                                                                        {
                                                                            declinableApplicationIds.length
                                                                        }
                                                                        )
                                                                    </Button>
                                                                )}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </>
                    )}

                    <section>
                        {/* Trustee Filters */}
                        {userDetails.data?.role === "trustee" && (
                            <div className="flex flex-wrap gap-2">
                                <CategorySelect
                                    value={category || "all"}
                                    onValueChange={(value) =>
                                        setFilterParams("category", value)
                                    }
                                />
                                <CountrySelect
                                    value={country || "all"}
                                    onValueChange={(value) =>
                                        setFilterParams("country", value)
                                    }
                                />
                                <AgeGroupSelect
                                    value={ageGroup || "all"}
                                    onValueChange={(value) =>
                                        setFilterParams("ageGroup", value)
                                    }
                                />
                            </div>
                        )}
                        {userDetails.data?.role !== "admin" && (
                            <div className="mb-4">
                                <TablePageNavigationButtons table={table} />
                            </div>
                        )}
                        <span className="hidden sm:flex text-sm text-muted-foreground italic items-center justify-end mb-2 mr-2">
                            <Info className="inline-block w-4 h-4 mr-1" />
                            Hold{" "}
                            <kbd className="font-mono text-xs font-bold bg-muted px-1 mx-1 py-0.5 rounded-sm">
                                SHIFT
                            </kbd>{" "}
                            key while scrolling within the table to scroll
                            horizontally
                        </span>
                        <DataTable
                            isFetching={applications.isFetching}
                            table={table}
                            userRole={userDetails.data?.role || "applicant"}
                            onSelectionChange={handleSelectionChange}
                        />
                    </section>
                </div>
            </div>

            <BulkDeclineModal
                open={isBulkDeclineModalOpen}
                onOpenChange={setIsBulkDeclineModalOpen}
                selectedApplicationIds={declinableApplicationIds}
                onSuccess={handleBulkDeclineSuccess}
            />
        </>
    )
}
